import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  adminApi, setAdminToken, getAdminToken,
  setAdminMe, hasAdminAuth,
} from '../lib/adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { ShieldCheck, KeyRound, Lock, Mail, Smartphone } from 'lucide-react';

const AdminLogin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState('email'); // 'email' | 'master'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mfa, setMfa] = useState(null); // {challenge_token, mobile_hint, dev_otp?}
  const [otp, setOtp] = useState('');

  if (hasAdminAuth()) {
    return <Navigate to="/admin" replace />;
  }

  const finishLogin = (data) => {
    // The httpOnly tgm_admin_jwt cookie is set by the server response (no JS needed).
    // We cache the user profile for instant UI render before /auth/me resolves.
    setAdminMe(data.user || null);
    toast({ title: `Welcome, ${data.user?.name || 'Admin'}` });
    navigate('/admin');
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return toast({ title: 'Enter email and password', variant: 'destructive' });
    try {
      setSubmitting(true);
      const data = await adminApi.authLogin(email.trim().toLowerCase(), password);
      if (data.mfa_required) {
        setMfa({ challenge_token: data.challenge_token, mobile_hint: data.mobile_hint, dev_otp: data.dev_otp });
        toast({ title: `OTP sent to ${data.mobile_hint}` });
      } else {
        finishLogin(data);
      }
    } catch (err) {
      toast({ title: 'Sign-in failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return toast({ title: 'Enter the OTP', variant: 'destructive' });
    try {
      setSubmitting(true);
      const data = await adminApi.authVerifyMfa(mfa.challenge_token, otp.trim());
      finishLogin(data);
    } catch (err) {
      toast({ title: 'MFA failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const submitMaster = async (e) => {
    e.preventDefault();
    if (!token.trim()) return toast({ title: 'Enter the master token', variant: 'destructive' });
    try {
      setSubmitting(true);
      setAdminToken(token.trim());
      await adminApi.ping();
      toast({ title: 'Welcome, Master', description: 'Master-token access granted.' });
      navigate('/admin');
    } catch (err) {
      localStorage.removeItem('tgmart_admin_token');
      toast({ title: 'Invalid master token', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1e293b] via-[#1f3f87] to-[#2c54b2] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 backdrop-blur border border-white/20 mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">
            <span className="text-[#ee5e23]">telangana</span>Mart Admin
          </h1>
          <p className="text-blue-100 text-sm mt-1">Secure dashboard access</p>
        </div>
        <div className="bg-white rounded-lg shadow-2xl p-6">
          {!mfa && (
            <div className="flex bg-gray-100 rounded-md p-1 mb-4 text-xs font-medium" data-testid="admin-login-tabs">
              <button
                type="button"
                onClick={() => setTab('email')}
                className={`flex-1 py-2 rounded transition-colors ${tab === 'email' ? 'bg-white shadow text-[#2c54b2]' : 'text-gray-600 hover:text-gray-900'}`}
                data-testid="admin-tab-email"
              >Email & Password</button>
              <button
                type="button"
                onClick={() => setTab('master')}
                className={`flex-1 py-2 rounded transition-colors ${tab === 'master' ? 'bg-white shadow text-[#2c54b2]' : 'text-gray-600 hover:text-gray-900'}`}
                data-testid="admin-tab-master"
              >Master Token</button>
            </div>
          )}

          {mfa ? (
            <form onSubmit={submitMfa} className="space-y-4" data-testid="admin-mfa-form">
              <div className="text-center">
                <Smartphone className="w-8 h-8 text-[#2c54b2] mx-auto" />
                <h2 className="text-lg font-bold text-gray-900 mt-2">Verify it&apos;s you</h2>
                <p className="text-xs text-gray-500">We&apos;ve sent a 6-digit code to <strong>{mfa.mobile_hint}</strong></p>
                {mfa.dev_otp && (
                  <div className="text-xs bg-yellow-50 border border-yellow-200 rounded px-2 py-1 inline-block mt-2">
                    Dev OTP: <span className="font-mono font-bold">{mfa.dev_otp}</span>
                  </div>
                )}
              </div>
              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit OTP"
                inputMode="numeric"
                className="text-center font-mono text-lg tracking-widest"
                data-testid="admin-mfa-otp"
                autoFocus
              />
              <Button type="submit" disabled={submitting} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11" data-testid="admin-mfa-verify-btn">
                <ShieldCheck className="w-4 h-4 mr-2" /> {submitting ? 'Verifying…' : 'Verify & Sign In'}
              </Button>
              <button type="button" onClick={() => { setMfa(null); setOtp(''); }} className="w-full text-xs text-gray-500 hover:text-gray-900" data-testid="admin-mfa-back">
                ← Back to sign-in
              </button>
            </form>
          ) : tab === 'email' ? (
            <form onSubmit={submitEmail} className="space-y-4" data-testid="admin-email-form">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Admin sign in</h2>
              <p className="text-xs text-gray-500">Use your admin account email and password.</p>
              <div>
                <Label htmlFor="email" className="text-xs">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@telanganamart.com" className="pl-9" data-testid="admin-email-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="password" className="text-xs">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-9" data-testid="admin-password-input" />
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11" data-testid="admin-login-submit">
                <ShieldCheck className="w-4 h-4 mr-2" />
                {submitting ? 'Signing in…' : 'Sign In'}
              </Button>
            </form>
          ) : (
            <form onSubmit={submitMaster} className="space-y-4" data-testid="admin-master-form">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Master token sign in</h2>
              <p className="text-xs text-gray-500">For emergency/CLI use. Configure <code className="text-[#2c54b2]">ADMIN_TOKEN</code> in backend env.</p>
              <div>
                <Label htmlFor="token" className="text-xs">Master Token</Label>
                <div className="relative mt-1">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input id="token" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="tgmart-admin-secret-..." className="pl-9" data-testid="admin-master-input" />
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-[#2c54b2] hover:bg-[#1f3f87] text-white h-11" data-testid="admin-master-submit">
                <ShieldCheck className="w-4 h-4 mr-2" />
                {submitting ? 'Verifying…' : 'Continue with Master Token'}
              </Button>
            </form>
          )}
        </div>
        <div className="text-center mt-4">
          <a href="/" className="text-xs text-blue-100 hover:text-white transition-colors">← Back to TelanganaMart</a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
